import { FormInstance } from 'antd'
import { AnalyzeJobOfferInput } from '../../../prompts/input/analyze-job-offer-input'
import { AnalyzeJobOfferResult } from '../../../prompts/output/analyze-job-offer-result'
import { AssistantMessageInput } from '../../../prompts/input/assistant-message-input'
import { AgentType, CUSTOM_AGENT, INTERVIEW_AGENT, SALES_AGENT } from '../../agent-type'
import { StoreInput } from '../../input/store-input'
import { Permission, USE_CUSTOM_AGENT, USE_INTERVIEW_AGENT, USE_SALES_AGENT } from '../../../user/permission'
import { ChatMessageProps } from '../../../components/chat/chat'

type CampaignAgentConfig = {
    type: AgentType
    permission: Permission
    label: string
    description: string
    startingPoint: string
    setup: string
    internal?: boolean
    initialData: NonNullable<StoreInput['data']>
    buildAssistantInput: (form: FormInstance<StoreInput>, messages: ChatMessageProps[]) => AssistantMessageInput
    buildAnalyzeInput?: (form: FormInstance<StoreInput>) => AnalyzeJobOfferInput
    applyAnalyzeResult?: (form: FormInstance<StoreInput>, result: AnalyzeJobOfferResult) => void
}

export type CampaignCreateInitialValues = {
    title: string
    tools?: import('../../../app-connection/types').ToolSelection[]
    locale?: StoreInput['locale']
    allowLanguageSwitch?: boolean
    type?: AgentType
    data: StoreInput['data']
    accountId?: string | null
    formId?: string | null
    whatsappBusinessAccountId?: string | null
    phoneNumberId?: string | null
    startMessageTemplateId?: string | null
    recontactSchedule?: StoreInput['recontactSchedule']
}

export const campaignAgentConfig: Record<AgentType, CampaignAgentConfig> = {
    [INTERVIEW_AGENT]: {
        type: INTERVIEW_AGENT,
        permission: USE_INTERVIEW_AGENT,
        label: 'Interview',
        description: 'Screen candidates for a vacancy with questions suggested by Evaa.',
        startingPoint: 'A job posting',
        setup: 'Screening questions, rejection rules and follow-up questions',
        initialData: {
            vacancy: '',
            feedback: '',
            clarificationDepth: 'once',
            questions: [],
        },
        buildAssistantInput: (form, messages) => ({
            type: INTERVIEW_AGENT,
            title: form.getFieldValue('title') || '',
            data: {
                vacancy: form.getFieldValue(['data', 'vacancy']) || '',
                questions: form.getFieldValue(['data', 'questions']) || [],
                feedback: form.getFieldValue(['data', 'feedback']) ?? null,
                clarificationDepth: form.getFieldValue(['data', 'clarificationDepth']) || 'once',
            },
            startMessageTemplateId: form.getFieldValue('startMessageTemplateId'),
            messages,
            name: '',
            locale: form.getFieldValue('locale') || 'nl_NL',
            allowLanguageSwitch: form.getFieldValue('allowLanguageSwitch') || false,
        }),
        buildAnalyzeInput: (form) => ({
            offer: form.getFieldValue(['data', 'vacancy']),
            locale: form.getFieldValue('locale') || 'nl_NL',
            questions: form.getFieldValue(['data', 'questions']) || [],
        }),
        applyAnalyzeResult: (form, result) => {
            form.setFieldValue(
                ['data', 'questions'],
                result.questions.map((questionText) => ({
                    text: questionText,
                    autoReject: false,
                }))
            )
        },
    },
    [CUSTOM_AGENT]: {
        type: CUSTOM_AGENT,
        permission: USE_CUSTOM_AGENT,
        label: 'Custom conversation',
        description: 'Choose the goal, tone and approach. Evaa follows your instructions.',
        startingPoint: 'Your conversation instructions',
        setup: 'The goal, tone, approach and evaluation criteria',
        initialData: { prompt: '' },
        buildAssistantInput: (form, messages) => buildPromptAssistantInput(CUSTOM_AGENT, form, messages),
    },
    [SALES_AGENT]: {
        type: SALES_AGENT,
        permission: USE_SALES_AGENT,
        label: 'Evaa Sales',
        description: 'Sales conversations for Evaa, including account creation.',
        startingPoint: 'Your Evaa sales instructions',
        setup: 'The sales approach and when to offer an Evaa account',
        internal: true,
        initialData: { prompt: '' },
        buildAssistantInput: (form, messages) => buildPromptAssistantInput(SALES_AGENT, form, messages),
    },
}

function buildPromptAssistantInput(
    type: typeof CUSTOM_AGENT | typeof SALES_AGENT,
    form: FormInstance<StoreInput>,
    messages: ChatMessageProps[]
): AssistantMessageInput {
    return {
        type,
        ...(type === CUSTOM_AGENT ? { tools: form.getFieldValue('tools') || [] } : {}),
        title: form.getFieldValue('title') || '',
        startMessageTemplateId: form.getFieldValue('startMessageTemplateId') ?? null,
        data: { prompt: form.getFieldValue(['data', 'prompt']) ?? null },
        messages,
        name: '',
        locale: form.getFieldValue('locale') || 'nl_NL',
        allowLanguageSwitch: form.getFieldValue('allowLanguageSwitch') || false,
    }
}

export function availableCampaignAgentTypes(can: (permission: Permission | Permission[]) => boolean): AgentType[] {
    return Object.values(campaignAgentConfig)
        .filter((config) => can(config.permission))
        .map((config) => config.type)
}

export function getCampaignAgentConfig(type?: AgentType): CampaignAgentConfig | undefined {
    return type ? campaignAgentConfig[type] : undefined
}

export function getCampaignCreateInitialValues(availableTypes: AgentType[]): CampaignCreateInitialValues {
    const selectedType = availableTypes.length === 1 ? availableTypes[0] : undefined
    const selectedConfig = getCampaignAgentConfig(selectedType)

    return {
        title: '',
        tools: [],
        type: selectedType,
        data: selectedConfig?.initialData ?? campaignAgentConfig[INTERVIEW_AGENT].initialData,
    }
}

// t('Custom conversation')
// t('Evaa Sales')
// t('Screen candidates for a vacancy with questions suggested by Evaa.')
// t('A job posting')
// t('Screening questions, rejection rules and follow-up questions')
// t('Choose the goal, tone and approach. Evaa follows your instructions.')
// t('Your conversation instructions')
// t('The goal, tone, approach and evaluation criteria')
// t('Sales conversations for Evaa, including account creation.')
// t('Your Evaa sales instructions')
// t('The sales approach and when to offer an Evaa account')
